import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styles from "./WorkflowPhoneVerification.module.scss";
import { IntlTelInput } from "./IntlTelInput";
import {
  US_REGION_ISOS,
  isPremiumRateNumber,
  isUnassignedOrFictitiousNumber,
} from "../../shared/utils/phoneValidation";

interface Props {
  onSubmit: (phoneNumber: string) => void;
  /** Optional: when set, renders a "I don't have a US phone number" link → Trusted Referee. */
  onNoUsPhone?: () => void;
  isSubmitting: boolean;
}

export function WorkflowPhoneScreen({ onSubmit, onNoUsPhone, isSubmitting }: Props) {
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isVoip, setIsVoip] = useState(false);
  const [countryIso, setCountryIso] = useState("us");
  const [isDirty, setIsDirty] = useState(false);

  const handleChangeNumber = useCallback((num: string) => {
    setPhoneNumber(num);
    if (num) setIsDirty(true);
  }, []);

  const handleChangeValidity = useCallback((valid: boolean) => {
    setIsPhoneValid(valid);
  }, []);

  const handleChangeIsVoip = useCallback((voip: boolean) => {
    setIsVoip(voip);
  }, []);

  const handleChangeCountry = useCallback((iso2: string) => {
    setCountryIso(iso2);
  }, []);

  // Submittable only when the number is valid, not premium-rate, not VOIP, not an
  // unassigned/fictitious number, and from the US or a US territory.
  // Premium-rate is a property of the number's NPA (not the detected region), so it's
  // checked regardless of region — most premium NPAs are Caribbean nations that also
  // fail the US-region check, but this catches and labels them precisely.
  const isUsRegion = US_REGION_ISOS.includes(countryIso);
  const isPremiumRate = isPremiumRateNumber(phoneNumber);
  const isUnassignedOrFictitious = isUsRegion && !isPremiumRate && isUnassignedOrFictitiousNumber(phoneNumber);
  const canSubmit = isPhoneValid && isUsRegion && !isVoip && !isPremiumRate && !isUnassignedOrFictitious;

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit(phoneNumber);
  }, [canSubmit, phoneNumber, onSubmit]);

  // Error precedence: premium-rate → non-US → VOIP → generic invalid (covers both an
  // incomplete/malformed number and an unassigned/fictitious one).
  const showPremiumError = isDirty && isPremiumRate;
  const showNonUsError = !isUsRegion && !isPremiumRate;
  const showVoipError = isDirty && isVoip && isUsRegion && !isPremiumRate && !isUnassignedOrFictitious;
  const showError = showPremiumError || showNonUsError || (isDirty && isUsRegion && !isVoip && (!isPhoneValid || isUnassignedOrFictitious));
  const errorMessage = showPremiumError
    ? t("phone.screen.premiumRateError")
    : showNonUsError
      ? t("phone.screen.nonUsError")
      : t("phone.screen.validationError");

  return (
    <div className={styles.phoneCard}>
      <h1 className={styles.phoneTitle}>{t("phone.screen.title")}</h1>
      <p className={styles.phoneSubtitle}>{t("phone.screen.subtitle")}</p>

      <form onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label className={styles.fieldLabel} htmlFor="phone-input">
            {t("phone.screen.label")}
          </label>
          <IntlTelInput
            id="phone-input"
            onChangeNumber={handleChangeNumber}
            onChangeValidity={handleChangeValidity}
            onChangeIsVoip={handleChangeIsVoip}
            onChangeCountry={handleChangeCountry}
            hasError={showError || showVoipError}
            disabled={isSubmitting}
            ariaLabel={t("phone.screen.inputAriaLabel")}
            countryCodeAriaLabel={t("phone.screen.countryCodeAriaLabel")}
          />
          {showError && (
            <p className={styles.fieldError} role="alert">
              {errorMessage}
            </p>
          )}
          {showVoipError && (
            <div className={styles.voipError} role="alert">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: "1px" }}>
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#BA0000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="9" x2="12" y2="13" stroke="#BA0000" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="12" cy="17" r="0.5" fill="#BA0000" stroke="#BA0000" strokeWidth="0.5"/>
              </svg>
              <p className={styles.voipErrorText}>{t("phone.screen.voipError")}</p>
            </div>
          )}
        </div>

        <div className={styles.infoBox}>
          {t("phone.screen.disclaimer")}
        </div>

        <button
          type="submit"
          className={styles.continueButton}
          disabled={!canSubmit || isSubmitting}
        >
          {isSubmitting ? t("phone.screen.submittingButton") : t("phone.screen.continueButton")}
        </button>
      </form>

      {onNoUsPhone && (
        <button
          type="button"
          className={styles.noUsPhoneLink}
          onClick={onNoUsPhone}
          disabled={isSubmitting}
        >
          {t("phone.screen.noUsPhone")}
        </button>
      )}
    </div>
  );
}
